/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { store } from 'redux/store';
import { useNavigate, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import SearchList from '../search-list';
import WizardSteps from '../wizard-steps';
import SearchTabList from '../search-tab-list';
import AssetsKindList from '../assets-kind-list';
import Icon from '@components/icon-component';
import styles from './style.module.scss';

const AddNewInspection = ({ asset = false, setActive, setAddNew }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(0);
  const [assetKind, setAssetKind] = useState('');
  const [inspectionTypeId, setInspectionTypeId] = useState('');
  const [inspectionFormId, setInspectionFormId] = useState('');
  const [inspectionFormName, setInspectionFormName] = useState();
  const [equipment, setEquipment] = useState(null);
  const [idOf, setIdOf] = useState('');
  const { inspection = {}, cache } = store.getState();
  const { inspectionFormsMapWIthUTSection } = inspection;
  const { equipments = [], equipmentsMap } = cache;
  const pathname = location.pathname;

  useEffect(() => {
    if (asset) {
      setIdOf(pathname?.split('/')[2]);
      setAssetKind(
        _.capitalize(pathname.split('/')[1].replace('parent-', '').replace('-details', ''))
      );
    }
  }, [asset]);

  const inspectionTypeLists = useMemo(() => {
    const list =
      step === 0 && !asset
        ? []
        : getInspectionTypesList({ idOf, assetKind, equipments, equipmentsMap, inspection });
    return list;
  }, [assetKind, idOf, step, asset]);

  const equipmentCategories = useMemo(() => {
    const selectedForm = inspection?.inspectionFormsUserCache?.find(
      (form) => form?.inspectionFormId === inspectionFormId
    );
    return selectedForm?.equipmentCategories || [];
  }, [inspectionFormId]);

  const stepForm = [
    {
      title: 'What kind of inspection?',
      content: (
        <SearchList
          list={inspectionTypeLists || []}
          onSelect={(item, formName, props) => {
            if (asset) {
              setAddNew('');
              setActive(false);
              setTimeout(() => {
                navigate(
                  `/work-order/?from=${
                    location.pathname
                  }&type=new-inspection&inspectionType=${item}&inspectionFormId=${
                    props.inspectionFormId
                  }&${assetKind.toLowerCase()}Id=${idOf}&new-recordId=${uuidv4()}`
                );
              }, 0);
            } else {
              setInspectionTypeId(item);
              setInspectionFormId(props?.inspectionFormId);
              setInspectionFormName(formName);
              setStep((prev) => prev + 1);
            }
          }}
          searchClassName={styles.searchClassName}
        />
      )
    }
  ];

  const steps = asset
    ? stepForm
    : [
        {
          title: 'For what kind of asset?',
          content: (
            <AssetsKindList
              list={assetsList}
              onSelect={(item) => {
                setAssetKind(item);
                setStep((prev) => prev + 1);
              }}
            />
          )
        },
        ...stepForm,
        {
          title: `Find and select the ${assetKind.toLowerCase()}.`,
          content: (
            <SearchTabList
              equipmentCategories={equipmentCategories}
              selectedFormHaveUTSection={inspectionFormsMapWIthUTSection[inspectionTypeId] || false}
              label={assetKind}
              onSelect={(id) => {
                setAddNew('');
                setActive(false);
                setTimeout(() => {
                  navigate(
                    `/work-order/?from=${
                      location.pathname
                    }&type=new-inspection&inspectionType=${inspectionTypeId}&inspectionFormId=${inspectionFormId}&${assetKind.toLowerCase()}Id=${id}&new-recordId=${uuidv4()}`
                  );
                }, 0);
              }}
            />
          )
        },
        {
          title: 'New Inspection Added Successfully.',
          content: ''
        }
      ];

  const onClickStep = (step) => {
    if (step === 0) setAssetKind('');
    if (step <= 1) {
      setInspectionTypeId('');
      setInspectionFormId('');
    }
    if (step <= 1) setInspectionFormName('');
    setStep(step);
  };

  return (
    <div>
      <WizardSteps
        current={step}
        setStep={onClickStep}
        total={steps.length - 1}
        title={steps[step].title}
        content={steps[step].content}
        breadcrumbs={
          asset
            ? [inspectionFormName?.toString() || 'Inspection form']
            : [
                assetKind || 'Asset type',
                inspectionFormName?.toString() || 'Inspection form',
                equipment?.name || `Select ${assetKind}`,
                'Added'
              ]
        }
      />
    </div>
  );
};

const assetsList = [
  { icon: <Icon name={'Site'} />, title: 'Site' },
  { icon: <Icon name={'equipment-white'} />, title: 'Equipment' },
  { icon: <Icon name={'pipeline-white'} />, title: 'Pipeline' }
];

export default AddNewInspection;

const formMatchesEquipmentCategory = (form, equipmentCategory) => {
  const equipmentCategories = form?.equipmentCategories || [];
  return !equipmentCategories.length || equipmentCategories.includes(equipmentCategory);
};

const getInspectionTypesList = ({ idOf, assetKind, equipments, equipmentsMap, inspection }) => {
  const assetKindKey = assetKind?.toLowerCase();
  let equipmentCategory = null;

  if (idOf && assetKindKey === 'equipment') {
    equipmentCategory = equipments[equipmentsMap[idOf]]?.equipmentCategory || null;
  }

  const filteredForms =
    inspection?.inspectionFormsUserCache?.filter((form) => {
      const inspectionType = inspection?.inspectionTypes?.find((x) => x.key === form.inspectionType);
      if (!inspectionType?.availableOnDemand || !inspectionType?.[assetKindKey]) {
        return false;
      }

      if (equipmentCategory) {
        return formMatchesEquipmentCategory(form, equipmentCategory);
      }

      return true;
    }) || [];

  return filteredForms.map((form) => ({
    value: form.name,
    inspectionFormId: form.inspectionFormId,
    key: form.inspectionType
  }));
};
